import React, { useState, useEffect, useCallback } from 'react';
import GameHUD from '../shared/GameHUD';
import PauseMenu from '../shared/PauseMenu';
import GameComplete from '../GameComplete';
import { calculateRewards } from '../../../services/gameService';
import './WordPuzzleGame.css';

function WordPuzzleGame({
    gameContent,
    template,
    topic,
    difficulty = 'intermediate',
    onComplete,
    onQuit
}) {
    // Game state
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [guessedLetters, setGuessedLetters] = useState([]);
    const [wrongGuesses, setWrongGuesses] = useState(0);
    const [score, setScore] = useState(0);
    const [wordsCompleted, setWordsCompleted] = useState(0);
    const [hintsUsed, setHintsUsed] = useState(0);
    const [currentHintLevel, setCurrentHintLevel] = useState(0);
    const [remainingHints, setRemainingHints] = useState(3);
    const [lives, setLives] = useState(6);
    const [xpEarned, setXpEarned] = useState(0);
    const [coinsEarned, setCoinsEarned] = useState(0);

    // Word state
    const [showDefinition, setShowDefinition] = useState(false);
    const [wordSolved, setWordSolved] = useState(false);

    // UI state
    const [isPaused, setIsPaused] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [victory, setVictory] = useState(false);

    // Keyboard letters and numbers
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const numbers = '0123456789'.split('');

    // Current word data
    const currentWord = gameContent?.words?.[currentWordIndex];
    const word = currentWord?.word?.toUpperCase() || '';

    // Initialize game
    useEffect(() => {
        if (gameContent?.words) {
            const diffSettings = template?.difficultyScaling?.[difficulty] || {};
            setLives(diffSettings.lives || 6);
            setRemainingHints(diffSettings.hints || 3);
        }
    }, [gameContent, template, difficulty]);

    const handleWordSolved = useCallback(() => {
        setWordSolved(true);
        const bonus = Math.max(0, (6 - wrongGuesses) * 20);
        const hintPenalty = hintsUsed * 10;
        const wordScore = 100 + bonus - hintPenalty;
        setScore(prev => prev + Math.max(wordScore, 50));
        setWordsCompleted(prev => prev + 1);
        setXpEarned(prev => prev + 15);
        setShowDefinition(true);
    }, [wrongGuesses, hintsUsed]);

    // Check if word is solved
    useEffect(() => {
        if (word && !wordSolved) {
            const isSolved = word.split('').every(letter =>
                letter === ' ' || guessedLetters.includes(letter)
            );
            if (isSolved) {
                handleWordSolved();
            }
        }
    }, [guessedLetters, word, wordSolved, handleWordSolved]);

    const handleLetterGuess = (letter) => {
        if (
            guessedLetters.includes(letter) ||
            wordSolved ||
            isPaused ||
            isComplete
        ) return;

        setGuessedLetters(prev => [...prev, letter]);

        if (!word.includes(letter)) {
            const newWrongGuesses = wrongGuesses + 1;
            setWrongGuesses(newWrongGuesses);
            setLives(prev => {
                const newLives = prev - 1;
                if (newLives <= 0) {
                    handleWordFailed();
                }
                return newLives;
            });
        }
    };

    const handleWordFailed = () => {
        setWordSolved(true);
        setShowDefinition(true);
        // Reveal the word
        setGuessedLetters(word.split('').filter(l => l !== ' '));
    };

    const handleNextWord = () => {
        if (currentWordIndex + 1 < gameContent.words.length) {
            setCurrentWordIndex(prev => prev + 1);
            setGuessedLetters([]);
            setWrongGuesses(0);
            setCurrentHintLevel(0);
            setWordSolved(false);
            setShowDefinition(false);
            setLives(template?.difficultyScaling?.[difficulty]?.lives || 6);
        } else {
            handleGameOver(wordsCompleted > 0);
        }
    };

    const handleUseHint = () => {
        if (remainingHints <= 0 || !currentWord?.hints || wordSolved) return;

        const nextHintLevel = currentHintLevel + 1;
        if (nextHintLevel <= currentWord.hints.length) {
            setCurrentHintLevel(nextHintLevel);
            setRemainingHints(prev => prev - 1);
            setHintsUsed(prev => prev + 1);
        }
    };

    const handleGameOver = useCallback((won) => {
        setVictory(won);
        setIsComplete(true);

        const totalWords = gameContent?.words?.length || 0;
        const accuracy = totalWords > 0
            ? Math.round((wordsCompleted / totalWords) * 100)
            : 0;

        const rewards = calculateRewards(template, wordsCompleted, accuracy);

        setXpEarned(rewards.xp);
        setCoinsEarned(rewards.coins);

        if (onComplete) {
            onComplete({
                victory: won,
                score,
                xp: rewards.xp,
                coins: rewards.coins,
                stars: rewards.stars,
                accuracy,
                stats: {
                    wordsCompleted,
                    hintsUsed,
                    totalWords
                }
            });
        }
    }, [score, wordsCompleted, hintsUsed, gameContent, template, onComplete]);

    const handleRestart = () => {
        setCurrentWordIndex(0);
        setGuessedLetters([]);
        setWrongGuesses(0);
        setScore(0);
        setWordsCompleted(0);
        setHintsUsed(0);
        setCurrentHintLevel(0);
        setRemainingHints(template?.difficultyScaling?.[difficulty]?.hints || 3);
        setLives(template?.difficultyScaling?.[difficulty]?.lives || 6);
        setXpEarned(0);
        setCoinsEarned(0);
        setWordSolved(false);
        setShowDefinition(false);
        setIsPaused(false);
        setIsComplete(false);
        setVictory(false);
    };

    const getLetterClass = (letter) => {
        if (!guessedLetters.includes(letter)) return '';
        if (word.includes(letter)) return 'correct';
        return 'incorrect';
    };

    const renderWord = () => {
        return word.split('').map((letter, index) => {
            const isRevealed = letter === ' ' || guessedLetters.includes(letter);
            return (
                <span
                    key={index}
                    className={`word-letter ${isRevealed ? 'revealed' : ''} ${letter === ' ' ? 'space' : ''}`}
                >
                    {isRevealed ? letter : '_'}
                </span>
            );
        });
    };

    if (!gameContent) {
        return (
            <div className="wordpuzzle-loading">
                <div className="loading-spinner">📝</div>
                <p>Loading word puzzle...</p>
            </div>
        );
    }

    return (
        <div className="wordpuzzle-game">
            <GameHUD
                gameName="Word Wizard"
                score={score}
                lives={lives}
                maxLives={template?.difficultyScaling?.[difficulty]?.lives || 6}
                xp={xpEarned}
                coins={coinsEarned}
                onPause={() => setIsPaused(true)}
                showTimer={false}
            />

            <div className="wordpuzzle-info">
                <span className="wordpuzzle-topic">📚 {topic}</span>
                <span className="wordpuzzle-progress">
                    Word {currentWordIndex + 1} / {gameContent.words.length}
                </span>
            </div>

            <div className="wordpuzzle-content">
                {/* Category */}
                {currentWord?.category && (
                    <div className="word-category">
                        <span className="category-label">Category:</span>
                        <span className="category-value">{currentWord.category}</span>
                    </div>
                )}

                {/* Hint */}
                {currentHintLevel > 0 && currentWord?.hints && (
                    <div className="word-hint">
                        <span className="hint-icon">💡</span>
                        <span className="hint-text">
                            {currentWord.hints[currentHintLevel - 1]?.hint}
                        </span>
                    </div>
                )}

                {/* Word Display */}
                <div className="word-display">
                    {renderWord()}
                </div>

                {/* Wrong Guesses Indicator */}
                <div className="wrong-guesses">
                    <div className="hangman-figure">
                        {wrongGuesses >= 1 && <div className="hangman-part head">😵</div>}
                        {wrongGuesses >= 2 && <div className="hangman-part body"></div>}
                        {wrongGuesses >= 3 && <div className="hangman-part arm-left"></div>}
                        {wrongGuesses >= 4 && <div className="hangman-part arm-right"></div>}
                        {wrongGuesses >= 5 && <div className="hangman-part leg-left"></div>}
                        {wrongGuesses >= 6 && <div className="hangman-part leg-right"></div>}
                    </div>
                </div>

                {/* Keyboard */}
                {!wordSolved && (
                    <div className="keyboard-container">
                        <div className="keyboard">
                            {alphabet.map(letter => (
                                <button
                                    key={letter}
                                    className={`key ${getLetterClass(letter)} ${guessedLetters.includes(letter) ? 'used' : ''}`}
                                    onClick={() => handleLetterGuess(letter)}
                                    disabled={guessedLetters.includes(letter)}
                                >
                                    {letter}
                                </button>
                            ))}
                        </div>
                        <div className="keyboard numbers">
                            {numbers.map(num => (
                                <button
                                    key={num}
                                    className={`key ${getLetterClass(num)} ${guessedLetters.includes(num) ? 'used' : ''}`}
                                    onClick={() => handleLetterGuess(num)}
                                    disabled={guessedLetters.includes(num)}
                                >
                                    {num}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Hint Button */}
                {!wordSolved && remainingHints > 0 && (
                    <button
                        className="hint-button"
                        onClick={handleUseHint}
                        disabled={currentHintLevel >= (currentWord?.hints?.length || 0)}
                    >
                        💡 Use Hint ({remainingHints} left)
                    </button>
                )}

                {/* Definition Panel */}
                {showDefinition && (
                    <div className="definition-panel">
                        <div className={`result-badge ${lives > 0 ? 'success' : 'failed'}`}>
                            {lives > 0 ? '🎉 Correct!' : '📚 The word was:'}
                        </div>
                        <h3 className="revealed-word">{word}</h3>
                        {currentWord?.definition && (
                            <p className="word-definition">{currentWord.definition}</p>
                        )}
                        {currentWord?.funFact && (
                            <div className="fun-fact">
                                <span className="fact-icon">🌟</span>
                                <span>{currentWord.funFact}</span>
                            </div>
                        )}
                        <button className="next-word-btn" onClick={handleNextWord}>
                            {currentWordIndex + 1 < gameContent.words.length
                                ? 'Next Word →'
                                : 'See Results'
                            }
                        </button>
                    </div>
                )}
            </div>

            {/* Pause Menu */}
            {isPaused && (
                <PauseMenu
                    gameName="Word Wizard"
                    score={score}
                    stats={{
                        wordsCompleted,
                        hintsUsed
                    }}
                    onResume={() => setIsPaused(false)}
                    onRestart={handleRestart}
                    onQuit={onQuit}
                />
            )}

            {/* Game Complete */}
            {isComplete && (
                <GameComplete
                    victory={victory}
                    gameName="Word Wizard"
                    score={score}
                    xpEarned={xpEarned}
                    coinsEarned={coinsEarned}
                    stars={wordsCompleted === gameContent.words.length ? 3 :
                           wordsCompleted > gameContent.words.length * 0.5 ? 2 : 1}
                    accuracy={Math.round((wordsCompleted / gameContent.words.length) * 100)}
                    stats={{
                        wordsCompleted,
                        hintsUsed,
                        totalWords: gameContent.words.length
                    }}
                    onPlayAgain={handleRestart}
                    onBackToMap={onQuit}
                    topic={topic}
                />
            )}
        </div>
    );
}

export default WordPuzzleGame;
